/**
 * Mini-moteur de tweens : les animations de cartes (pose, retournement,
 * bataille, butin) sont des interpolations sequencees, pilotees par la boucle
 * de rendu. Chaque tween retourne une Promise, ce qui permet d'ecrire les
 * sequences de jeu en async/await dans le controleur.
 */

export type Ease = (t: number) => number;

export const easeInOut: Ease = (t) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);
export const easeOut: Ease = (t) => 1 - (1 - t) ** 3;
export const easeIn: Ease = (t) => t * t * t;

interface ActiveTween {
  key?: string;
  elapsed: number;
  duration: number;
  ease: Ease;
  onUpdate: (t: number) => void;
  resolve: () => void;
}

export class Tweens {
  private active: ActiveTween[] = [];

  /**
   * Lance un tween ; `onUpdate` recoit la progression [0..1] deja passee par `ease`.
   * Avec `key`, tout tween actif portant la meme cle est annule au prealable : deux
   * animations qui ecrivent sur le meme objet (ex: survols rapides qui se succedent
   * avant la fin du precedent) ne se battent plus image par image pour la position
   * finale, la plus recente remplace proprement l'ancienne.
   */
  run(duration: number, onUpdate: (t: number) => void, ease: Ease = easeInOut, key?: string): Promise<void> {
    if (key !== undefined) this.cancel(key);
    return new Promise((resolve) => {
      this.active.push({ key, elapsed: 0, duration, ease, onUpdate, resolve });
    });
  }

  /** Annule le(s) tween(s) actif(s) portant cette cle, sans jouer leur derniere image. */
  cancel(key: string): void {
    const remaining: ActiveTween[] = [];
    for (const tween of this.active) {
      if (tween.key === key) tween.resolve();
      else remaining.push(tween);
    }
    this.active = remaining;
  }

  /** Simple attente calee sur la boucle de rendu (pause dramatique de la bataille). */
  wait(duration: number): Promise<void> {
    return this.run(duration, () => {});
  }

  /** Fait avancer tous les tweens ; a appeler a chaque frame avec le delta en secondes.
   * Retourne `true` si au moins un tween etait actif cette frame (utilise par le rendu
   * pour savoir si la geometrie a pu bouger, ex: invalidation de la carte d'ombres). */
  update(dt: number): boolean {
    if (this.active.length === 0) return false;
    // Copie : un onUpdate/resolve peut demarrer de nouveaux tweens pendant l'iteration.
    const list = this.active;
    const finished: ActiveTween[] = [];
    for (const tween of [...list]) {
      tween.elapsed += dt;
      const t = Math.min(1, tween.elapsed / tween.duration);
      tween.onUpdate(tween.ease(t));
      if (t >= 1) finished.push(tween);
    }
    this.active = this.active.filter((t) => !finished.includes(t));
    for (const tween of finished) tween.resolve();
    return true;
  }
}
